import { Fragment } from "react"
import { Box, Chip, Typography } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PaymentIcon from "@mui/icons-material/Payment"
import FactCheckIcon from "@mui/icons-material/FactCheck"
import EmailIcon from "@mui/icons-material/Email"

import { formatFullDateTime } from "../../../utils/format-date"
import {
  OrderHistoryEvent,
  OrderHistoryStage,
  OrderHistoryStatus,
  orderHistoryStage,
  orderHistoryStageSource,
  orderHistoryStatusLabel,
} from "../Orders/orderTypes"
import { OrderDetailCard } from "./OrderDetailCard"

type StatusColor = "primary" | "success" | "warning" | "error"

const statusColor: Record<OrderHistoryStatus, StatusColor> = {
  order_placed: "primary",
  payment_pending: "warning",
  payment_paid: "success",
  payment_canceled: "error",
  query_success: "success",
  query_unavailable: "error",
  query_emailed: "success",
}

const stageIcon: Record<OrderHistoryStage, JSX.Element> = {
  order: <ShoppingCartIcon fontSize="small" />,
  payment: <PaymentIcon fontSize="small" />,
  query: <FactCheckIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
}

const timelineColumns = { xs: '36px 1fr', md: '1fr 36px 1fr' }

type EventMarkerProps = {
  color: StatusColor
  isCurrent: boolean
  gridRow: { xs: number; md: number }
}

function EventMarker({ color, isCurrent, gridRow }: EventMarkerProps) {
  const theme = useTheme()
  const main = theme.palette[color].main

  return (
    <Box
      data-testid="timeline-event-marker"
      data-current={isCurrent}
      aria-hidden
      sx={{
        gridColumn: { xs: 1, md: 2 },
        gridRow,
        justifySelf: 'center',
        alignSelf: 'start',
        mt: '6px',
        zIndex: 1,
        width: 12,
        height: 12,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: main,
        bgcolor: isCurrent ? main : 'background.paper',
        boxShadow: `0 0 0 4px ${alpha(main, 0.15)}`,
        ...(isCurrent && {
          '@keyframes timelinePulse': {
            '0%': { boxShadow: `0 0 0 0 ${alpha(main, 0.5)}` },
            '100%': { boxShadow: `0 0 0 10px ${alpha(main, 0)}` },
          },
          animation: 'timelinePulse 1.6s ease-out infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }),
      }}
    />
  )
}

type HistoryStageGroup = {
  stage: OrderHistoryStage
  events: OrderHistoryEvent[]
}

function groupByStage(history: OrderHistoryEvent[]): HistoryStageGroup[] {
  return history.reduce<HistoryStageGroup[]>((groups, event) => {
    const stage = orderHistoryStage[event.status]
    const lastGroup = groups[groups.length - 1]

    if (lastGroup?.stage === stage) {
      lastGroup.events.push(event)
      return groups
    }

    return [...groups, { stage, events: [event] }]
  }, [])
}

type OrderHistoryTimelineProps = {
  history: OrderHistoryEvent[]
}

export function OrderHistoryTimeline({ history }: OrderHistoryTimelineProps) {
  const groups = groupByStage(history)

  return (
    <OrderDetailCard title="Histórico de Transações do Pedido">
      <Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: timelineColumns, columnGap: 2, mx: 'auto', width: '100%', maxWidth: 960 }}>
          <Box sx={{ gridColumn: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Chip size="small" variant="outlined" label="Início" sx={{ maxWidth: 'none', flexShrink: 0 }} />
            <Box sx={{ height: 24, borderLeft: '2px dashed', borderColor: 'grey.400', mt: 0.5 }} />
          </Box>
        </Box>

        <Box component="ol" aria-label="Etapas do pedido" sx={{ listStyle: 'none', my: 0, mx: 'auto', p: 0, width: '100%', maxWidth: 960 }}>
          {groups.map((group, index) => {
            const isLast = index === groups.length - 1
            const lastEvent = group.events[group.events.length - 1]
            const eventCount = group.events.length

            return (
              <Box
                component="li"
                key={`${group.stage}-${index}`}
                sx={{ display: 'grid', gridTemplateColumns: timelineColumns, columnGap: 2, rowGap: { xs: 0.5, md: 1.5 } }}
              >
                <Box
                  sx={{
                    gridColumn: { xs: 1, md: 2 },
                    gridRow: { xs: `1 / span ${1 + eventCount * 2}`, md: `1 / span ${1 + eventCount}` },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      color: 'common.white',
                      bgcolor: `${statusColor[lastEvent.status]}.main`,
                    }}
                  >
                    {stageIcon[group.stage]}
                  </Box>
                  <Box sx={{ flex: 1, borderLeft: '2px dashed', borderColor: 'grey.400', my: 0.5 }} />
                </Box>

                <Typography variant="subtitle2" sx={{ gridColumn: { xs: 2, md: 3 }, gridRow: 1, lineHeight: '36px' }}>
                  {orderHistoryStageSource[group.stage]}
                </Typography>

                {group.events.map((event, eventIndex) => {
                  const isLastEvent = eventIndex === eventCount - 1

                  return (
                    <Fragment key={`${event.status}-${event.date}`}>
                      <EventMarker
                        color={statusColor[event.status]}
                        isCurrent={isLast && isLastEvent}
                        gridRow={{ xs: 3 + eventIndex * 2, md: 2 + eventIndex }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          gridColumn: { xs: 2, md: 1 },
                          gridRow: { xs: 2 + eventIndex * 2, md: 2 + eventIndex },
                          textAlign: { xs: 'left', md: 'right' },
                          lineHeight: { md: '24px' },
                        }}
                      >
                        {formatFullDateTime(event.date)}
                      </Typography>

                      <Box
                        sx={{
                          gridColumn: { xs: 2, md: 3 },
                          gridRow: { xs: 3 + eventIndex * 2, md: 2 + eventIndex },
                          pb: isLastEvent && !isLast ? 5 : 0,
                        }}
                      >
                        <Chip size="small" label={orderHistoryStatusLabel[event.status]} color={statusColor[event.status]} />
                        {event.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {event.description}
                          </Typography>
                        )}
                      </Box>
                    </Fragment>
                  )
                })}
              </Box>
            )
          })}
        </Box>
      </Box>
    </OrderDetailCard>
  )
}
